/**
 * MIRROR of `mju-ai-workbench/shared/schemas/nodeManifest.ts` (branch
 * `feat/agent-node-supervisor`, uncommitted in its worktree on 2026-09-24):
 * shapes, limits and defaults are identical. One deliberate difference:
 * this package runs in the browser too, so {@link manifestRevision} uses
 * WebCrypto and is async; it produces the same hex digest as the
 * workbench's synchronous `node:crypto` version.
 *
 * The node manifest: what a headless workbench node (`mojoup-node`,
 * desktop/src/node/) tells its organisation it can do. Sent whole as
 * `capabilities` on the fleet registration (src/platform/fleet.ts) and,
 * for approval, on the device-code grant of `mojoup-node join`; the
 * heartbeat carries only its {@link manifestRevision}, so the platform can
 * tell "unchanged" from "the surface grew, approve it again"
 * (docs/plans/2026-09-agent-nodes.md, "Joining" and "Re-approval").
 *
 *   {
 *     "schemaVersion": 1,
 *     "nodeId": "dev_…",                     the device id the platform issued (the install id before approval)
 *     "name": "gpu-box-1",
 *     "kind": "node",
 *     "roles": ["teams", "inference"],        what the node is offered for; default ["teams"]
 *     "owner": { "type": "org", "id": "…", "tags": ["tag:gpu"] },
 *     "runtimes": [{ "id": "claude-code", "version": "2.1.0", "authenticated": true }],
 *     "models":   [{ "id": "qwen2.5-coder-14b", "contextLength": 32768, "acceleration": "gpu", "service": "model" }],
 *     "memory":   [{ "id": "graphify:root-1", "kind": "graphify", "scopes": ["repo", "knowledge"], "root": "root-1" }],
 *     "repos":    [{ "rootId": "root-1", "remote": "https://github.com/…", "branch": "main" }],
 *     "resources": { "cpu": 16, "ramGb": 64, "gpus": [{ "name": "RTX 4090", "vramGb": 24 }] },
 *     "services":  { "control": 7434, "model": 8080 },
 *     "policy":    { "acceptsDispatchFrom": ["project:prj_9f3…"], "maxConcurrentAgents": 4 },
 *     "sandbox":   { "engine": "docker", "images": ["ghcr.io/…"], "maxConcurrent": 2, "egress": "policy" },
 *     "computer":  { "kind": "browser", "display": "headless", "sessions": 2 }
 *   }
 *
 * Every field past `kind` describes an *offer*, not a grant: assignment and
 * approval live on the platform, and a node never widens its own audience.
 * Unknown fields are stripped rather than refused so an older platform
 * accepts a newer node.
 *
 * The workbench's file is the source; change both in the same piece of work
 * (shapes, limits and the revision algorithm).
 */
import { z } from "zod";
export const NODE_MANIFEST_SCHEMA_VERSION = 1;
/** What a node is offered for: agent teams, shared inference, the DLP classifier, agent sandboxes, remote computer use. */
export const nodeRoleSchema = z.enum(["teams", "inference", "dlp", "sandbox", "computer"]);
/** Who the node belongs to: a person's own box, the organisation, or one project. */
export const nodeOwnerSchema = z.object({
    type: z.enum(["user", "org", "project"]),
    id: z.string().min(1).max(200).optional(),
    /** Mesh policy tags (`tag:gpu`); the approver may edit them. */
    tags: z.array(z.string().min(1).max(64)).max(32).default([]),
});
/** An agent runtime installed on the node, and whether its CLI is signed in there. */
export const nodeRuntimeSchema = z.object({
    id: z.string().min(1).max(64),
    version: z.string().max(100).optional(),
    authenticated: z.boolean().optional(),
});
/** A model the node serves (the bundled local runner today). */
export const nodeModelSchema = z.object({
    id: z.string().min(1).max(200),
    contextLength: z.number().int().positive().max(10_000_000).optional(),
    acceleration: z.enum(["cpu", "gpu"]).optional(),
    /** The `services` entry that serves it (`model`). */
    service: z.string().min(1).max(64).optional(),
});
/** Memory the node can offer: a graphify graph of a root, or an Obsidian vault. */
export const nodeMemorySchema = z.object({
    id: z.string().min(1).max(200),
    kind: z.enum(["obsidian", "graphify"]),
    scopes: z.array(z.enum(["repo", "knowledge"])).min(1).max(2),
    /** The root it belongs to, for a graph. */
    root: z.string().min(1).max(200).optional(),
});
/** A repository root open on the node. */
export const nodeRepoSchema = z.object({
    rootId: z.string().min(1).max(200),
    remote: z.string().max(500).optional(),
    branch: z.string().max(200).optional(),
});
export const nodeGpuSchema = z.object({
    name: z.string().min(1).max(200),
    vramGb: z.number().nonnegative().max(100_000).optional(),
});
export const nodeResourcesSchema = z.object({
    /** Logical CPU count. */
    cpu: z.number().int().positive().max(100_000).optional(),
    ramGb: z.number().nonnegative().max(1_000_000).optional(),
    gpus: z.array(nodeGpuSchema).max(16).default([]),
});
const portSchema = z.number().int().min(1).max(65_535);
/** Loopback ports the node serves each surface on; the mesh agent exposes them on the overlay. */
export const nodeServicesSchema = z.object({
    control: portSchema.optional(),
    model: portSchema.optional(),
    memory: portSchema.optional(),
    dlp: portSchema.optional(),
    sandbox: portSchema.optional(),
    computer: portSchema.optional(),
});
export const nodePolicySchema = z.object({
    /** Scopes the node itself accepts dispatch from (`project:<id>`, `group:<name>`, `user:<oid>`, `tenant`); empty = whatever the assignment says. */
    acceptsDispatchFrom: z.array(z.string().min(1).max(200)).max(64).default([]),
    maxConcurrentAgents: z.number().int().positive().max(1_000).optional(),
});
/** Agent sandboxes the node can run (role `sandbox`). */
export const nodeSandboxSchema = z.object({
    engine: z.enum(["docker", "podman", "kubernetes"]),
    images: z.array(z.string().min(1).max(300)).max(32),
    maxConcurrent: z.number().int().positive().max(1_000).optional(),
    /** `policy`: egress through the node's policy; `none`: no network at all. */
    egress: z.enum(["policy", "none"]).optional(),
});
/** Remote computer use the node offers (role `computer`). */
export const nodeComputerSchema = z.object({
    kind: z.enum(["browser", "desktop"]),
    display: z.enum(["headless", "xvfb", "vnc", "native"]).optional(),
    sessions: z.number().int().positive().max(1_000).optional(),
});
/**
 * Whether the machine can run agents in a sandbox (the sandboxed Linux node,
 * mju-aioe/docs/plans/2026-10-sandboxed-node.md): the node's start-up check —
 * a seccomp notification listener can be installed, Landlock ABI ≥ 3, Docker
 * answers, NVIDIA OpenShell installed (desktop/src/node/sandbox/capability.ts).
 * `agents.sandbox: required` places work only on a node that reports
 * `canSandbox`. Separate from `sandbox`, which is the older offer of sandbox
 * images (role `sandbox`).
 */
export const nodeSandboxCheckSchema = z.object({
    canSandbox: z.boolean(),
    /** One sentence: why it cannot. */
    reason: z.string().max(300).optional(),
    kernel: z.string().max(200),
    landlockAbi: z.number().int().nonnegative().max(1_000).optional(),
    /** The OpenShell version. */
    openshell: z.string().max(100).optional(),
    docker: z.boolean().optional(),
});
export const nodeManifestSchema = z.object({
    schemaVersion: z.literal(NODE_MANIFEST_SCHEMA_VERSION),
    nodeId: z.string().min(1).max(100),
    name: z.string().min(1).max(200),
    kind: z.literal("node"),
    roles: z.array(nodeRoleSchema).max(5).default(["teams"]),
    owner: nodeOwnerSchema,
    runtimes: z.array(nodeRuntimeSchema).max(64).default([]),
    models: z.array(nodeModelSchema).max(64).default([]),
    memory: z.array(nodeMemorySchema).max(64).default([]),
    repos: z.array(nodeRepoSchema).max(200).default([]),
    resources: nodeResourcesSchema.prefault({}),
    services: nodeServicesSchema.prefault({}),
    policy: nodePolicySchema.prefault({}),
    sandbox: nodeSandboxSchema.optional(),
    computer: nodeComputerSchema.optional(),
    /**
     * The node's home: the directory it clones repositories into and runs agents from. AIOE-first
     * (the workbench's manifest should send it); shown on the node's page, never acted on.
     */
    home: z.string().min(1).max(1000).optional(),
    sandboxCheck: nodeSandboxCheckSchema.optional(),
});
/** JSON with every object's keys sorted, recursively; arrays keep their order. */
export function canonicalJson(value) {
    return JSON.stringify(value, (_key, v) => {
        if (v && typeof v === "object" && !Array.isArray(v)) {
            const out = {};
            for (const k of Object.keys(v).sort()) {
                const item = v[k];
                if (item !== undefined)
                    out[k] = item;
            }
            return out;
        }
        return v;
    });
}
/**
 * A stable revision of a manifest: the lower-case hex SHA-256 of its
 * canonical JSON after parsing (so defaults are applied and unknown fields
 * dropped). Key order never matters; array order does, so a builder sorts
 * its lists before asking. Throws on a manifest the schema refuses.
 */
export async function manifestRevision(manifest) {
    const parsed = nodeManifestSchema.parse(manifest);
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalJson(parsed)));
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
//# sourceMappingURL=nodeManifest.js.map